import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { format, subDays, addDays, isToday } from 'date-fns'
import {
  ChevronLeft, ChevronRight, Users, UserCheck, UserX, LogOut, Home, Calendar,
  CheckCircle2, TrendingUp, AlertCircle, Clock, Moon, Bell, X, Loader2, Trash2, Hash, Search, Check,
} from 'lucide-react'
import { getDailyAttendance } from '../../api/attendance'
import { listSlackChannels } from '../../api/organizations'
import { sendNotification, listBroadcasts, deleteBroadcast } from '../../api/notifications'
import { listPendingApprovals, reviewApproval, type TimedRequest } from '../../api/attendanceApprovals'
import type { UserDailyAttendance } from '../../types'
import StatTile from '@/components/dashboard/StatTile'

/** Human phrase for a timed request, e.g. "check in at 9:00 AM (WFH)". */
function requestPhrase(r: TimedRequest): string {
  const t = new Date(r.requested_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
  if (r.kind === 'checkin') return `check in at ${t}${r.wfh ? ' (WFH)' : ''}`
  if (r.kind === 'checkout') return `check out at ${t}`
  if (r.kind === 'late_checkout') {
    const day = new Date(`${r.date}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })
    // Already checked out at this time; rejecting resets it to the shift end.
    return `check out late for ${day} at ${t}`
  }
  return `correct auto-checkout to ${t}`
}

/** "Pending time requests" panel — admins approve/reject timed check-in/out requests. */
function PendingTimeRequests() {
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const qc = useQueryClient()
  const { data: requests = [] } = useQuery({
    queryKey: ['attendance-approvals'],
    queryFn: listPendingApprovals,
    refetchInterval: 45_000,
  })
  const [err, setErr] = useState('')
  const mut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'approved' | 'rejected' }) => reviewApproval(id, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['attendance-approvals'] })
      qc.invalidateQueries({ queryKey: ['attendance', 'daily'] })
      qc.invalidateQueries({ queryKey: ['notifications'] })
    },
    onError: (e: any) => setErr(e?.response?.data?.detail || 'Could not update the request'),
  })

  if (requests.length === 0) return null

  return (
    <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50/60 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Clock className="h-4 w-4 text-amber-600" />
        <h2 className="text-sm font-semibold text-gray-800">Pending time requests</h2>
        <span className="text-xs text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">{requests.length}</span>
      </div>
      {err && <div className="mb-2 rounded bg-red-50 border border-red-200 text-red-600 text-xs px-3 py-2">{err}</div>}
      <ul className="space-y-2">
        {requests.map((r) => (
          <li key={r.id} className="flex items-center gap-3 rounded-lg bg-white border border-gray-100 px-3 py-2">
            {r.user_avatar_url ? (
              <img src={r.user_avatar_url} alt="" className="h-7 w-7 rounded-full object-cover shrink-0" />
            ) : (
              <span className="h-7 w-7 rounded-full bg-[#EC5B2C]/15 text-[#EC5B2C] text-xs font-semibold flex items-center justify-center shrink-0">
                {(r.user_name || '?').charAt(0).toUpperCase()}
              </span>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm text-gray-800">
                <Link to={`/${orgSlug}/admin/users/${r.user_id}`} className="font-medium hover:text-[#EC5B2C] hover:underline">
                  {r.user_name || 'Employee'}
                </Link>{' '}
                wants to <span className="font-medium">{requestPhrase(r)}</span>
              </p>
              <p className="text-[11px] text-gray-400">
                {new Date(r.date + 'T00:00:00').toLocaleDateString()}{r.reason ? ` · ${r.reason}` : ''}
              </p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                disabled={mut.isPending}
                onClick={() => mut.mutate({ id: r.id, status: 'approved' })}
                className="inline-flex items-center gap-1 rounded-lg bg-green-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
              ><Check className="h-3.5 w-3.5" /> Approve</button>
              <button
                disabled={mut.isPending}
                onClick={() => mut.mutate({ id: r.id, status: 'rejected' })}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              ><X className="h-3.5 w-3.5" /> Reject</button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

function formatTime(isoStr?: string): string {
  if (!isoStr) return '—'
  return new Date(isoStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
}

function formatDuration(minutes?: number): string {
  if (!minutes || minutes <= 0) return '—'
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

const leaveLabels: Record<string, string> = {
  casual: 'Casual Leave',
  sick: 'Sick Leave',
  urgent: 'Urgent Leave',
  annual: 'Annual Leave',
  wfh: 'Work From Home',
}

const leaveColors: Record<string, string> = {
  casual: 'bg-blue-100 text-blue-700',
  sick: 'bg-red-100 text-red-700',
  urgent: 'bg-orange-100 text-orange-700',
  annual: 'bg-purple-100 text-purple-700',
  wfh: 'bg-teal-100 text-teal-700',
}

function StatusBadge({ user }: { user: UserDailyAttendance }) {
  const lateBadge = user.is_late && user.late_minutes ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
      Late {user.late_minutes >= 60 ? `${Math.floor(user.late_minutes / 60)}h ${user.late_minutes % 60}m` : `${user.late_minutes}m`}
    </span>
  ) : null

  // Leave takes precedence (unless user also worked)
  if (user.leave_type && !user.checked_in) {
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${leaveColors[user.leave_type] || 'bg-gray-100 text-gray-600'}`}>
        {user.leave_type === 'wfh' ? <Home className="h-3 w-3" /> : <Calendar className="h-3 w-3" />}
        {leaveLabels[user.leave_type] || user.leave_type}
      </span>
    )
  }

  const ds = user.day_status
  const badge = (() => {
    switch (ds) {
      case 'working':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <Clock className="h-3 w-3" /> Working
          </span>
        )
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
            <CheckCircle2 className="h-3 w-3" /> Completed
          </span>
        )
      case 'overtime':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
            <TrendingUp className="h-3 w-3" /> Overtime
          </span>
        )
      case 'less_hours':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
            <AlertCircle className="h-3 w-3" /> Less Hours
          </span>
        )
      case 'absent':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
            <UserX className="h-3 w-3" /> Absent
          </span>
        )
      case 'off':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
            <Moon className="h-3 w-3" /> Off
          </span>
        )
      case 'wfh':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-700">
            <Home className="h-3 w-3" /> WFH
          </span>
        )
      case 'leave':
        return (
          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${user.leave_type ? (leaveColors[user.leave_type] || 'bg-orange-100 text-orange-700') : 'bg-orange-100 text-orange-700'}`}>
            <Calendar className="h-3 w-3" /> {user.leave_type ? (leaveLabels[user.leave_type] || 'Leave') : 'Leave'}
          </span>
        )
      default:
        // Fallback to legacy rendering if day_status is missing
        if (!user.checked_in) {
          return (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              Not In
            </span>
          )
        }
        if (user.status === 'completed') {
          return (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
              Checked Out
            </span>
          )
        }
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
            Checked In
          </span>
        )
    }
  })()

  return (
    <div className="flex flex-wrap items-center gap-1">
      {badge}
      {lateBadge}
    </div>
  )
}

type Filter = 'all' | 'working' | 'completed' | 'overtime' | 'less_hours' | 'absent' | 'off' | 'leave' | 'wfh'

export default function Attendance() {
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const [selectedDate, setSelectedDate] = useState(() => new Date())
  const [filter, setFilter] = useState<Filter>('all')
  const [showNotify, setShowNotify] = useState(false)
  const [search, setSearch] = useState('')

  const dateStr = format(selectedDate, 'yyyy-MM-dd')
  const todayDate = new Date()
  const isCurrent = isToday(selectedDate)

  const { data = [], isLoading, error } = useQuery<UserDailyAttendance[]>({
    queryKey: ['attendance', 'daily', dateStr],
    queryFn: () => getDailyAttendance(dateStr),
    refetchInterval: 60_000,
  })

  const byStatus = (s: string) => data.filter((u) => u.day_status === s)
  const working = byStatus('working')
  const completed = byStatus('completed')
  const overtime = byStatus('overtime')
  const lessHours = byStatus('less_hours')
  const absent = byStatus('absent')
  const off = byStatus('off')
  const onLeave = byStatus('leave')
  const onWfh = byStatus('wfh')

  const filteredData =
    filter === 'working' ? working
    : filter === 'completed' ? completed
    : filter === 'overtime' ? overtime
    : filter === 'less_hours' ? lessHours
    : filter === 'absent' ? absent
    : filter === 'off' ? off
    : filter === 'leave' ? onLeave
    : filter === 'wfh' ? onWfh
    : data

  const roleLabelMap: Record<string, string> = {
    team_member: 'Team Member',
    project_lead: 'Project Lead',
    manager: 'Manager',
    admin: 'Admin',
  }

  // Employee search over the currently-filtered rows (name, role, status).
  const q = search.trim().toLowerCase()
  const visibleRows = q
    ? filteredData.filter((u) =>
        [u.name, roleLabelMap[u.role] || u.role, u.day_status].filter(Boolean).join(' ').toLowerCase().includes(q))
    : filteredData

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Attendance</h1>
        <button
          onClick={() => setShowNotify(true)}
          className="flex items-center gap-2 rounded-lg bg-[#EC5B2C] px-4 py-2 text-sm font-medium text-white hover:bg-[#d54e22]"
        >
          <Bell className="h-4 w-4" /> Send Notification
        </button>
      </div>
      {showNotify && <SendNotificationModal onClose={() => setShowNotify(false)} />}

      <PendingTimeRequests />

      {/* Date navigation */}
      <div className="flex items-center justify-center gap-2 sm:gap-3 mb-6">
        <button
          onClick={() => setSelectedDate((d) => subDays(d, 1))}
          className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
        >
          <ChevronLeft className="w-4 h-4 text-gray-600" />
        </button>
        <div className="text-center min-w-0">
          <p className="text-sm sm:text-lg font-semibold text-gray-900">
            {format(selectedDate, 'EEE, MMM d, yyyy')}
          </p>
          {isCurrent && <p className="text-xs text-brand font-medium">Today</p>}
        </div>
        <button
          onClick={() => setSelectedDate((d) => addDays(d, 1))}
          disabled={isCurrent}
          className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronRight className="w-4 h-4 text-gray-600" />
        </button>
        {!isCurrent && (
          <button
            onClick={() => setSelectedDate(todayDate)}
            className="ml-2 px-3 py-1.5 text-sm font-medium text-brand border border-brand/30 rounded-lg hover:bg-brand/10 transition-colors"
          >
            Today
          </button>
        )}
      </div>

      {/* Stat filters */}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-9 gap-2.5 sm:gap-3 mb-5">
        <StatTile label="Total" value={data.length} icon={<Users className="w-4 h-4" />} tone="blue" active={filter === 'all'} onClick={() => setFilter('all')} />
        <StatTile label="Working" value={working.length} icon={<UserCheck className="w-4 h-4" />} tone="green" active={filter === 'working'} onClick={() => setFilter((f) => f === 'working' ? 'all' : 'working')} />
        <StatTile label="Completed" value={completed.length} icon={<LogOut className="w-4 h-4" />} tone="purple" active={filter === 'completed'} onClick={() => setFilter((f) => f === 'completed' ? 'all' : 'completed')} />
        <StatTile label="Overtime" value={overtime.length} icon={<TrendingUp className="w-4 h-4" />} tone="indigo" active={filter === 'overtime'} onClick={() => setFilter((f) => f === 'overtime' ? 'all' : 'overtime')} />
        <StatTile label="Less Hours" value={lessHours.length} icon={<AlertCircle className="w-4 h-4" />} tone="amber" active={filter === 'less_hours'} onClick={() => setFilter((f) => f === 'less_hours' ? 'all' : 'less_hours')} />
        <StatTile label="On Leave" value={onLeave.length} icon={<Calendar className="w-4 h-4" />} tone="orange" active={filter === 'leave'} onClick={() => setFilter((f) => f === 'leave' ? 'all' : 'leave')} />
        <StatTile label="WFH" value={onWfh.length} icon={<Home className="w-4 h-4" />} tone="teal" active={filter === 'wfh'} onClick={() => setFilter((f) => f === 'wfh' ? 'all' : 'wfh')} />
        <StatTile label="Absent" value={absent.length} icon={<UserX className="w-4 h-4" />} tone="red" active={filter === 'absent'} onClick={() => setFilter((f) => f === 'absent' ? 'all' : 'absent')} />
        <StatTile label="Off" value={off.length} icon={<Moon className="w-4 h-4" />} tone="gray" active={filter === 'off'} onClick={() => setFilter((f) => f === 'off' ? 'all' : 'off')} />
      </div>

      {/* Search toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="relative flex-1 sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search employee by name, role, status…"
            className="w-full pl-9 pr-8 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#EC5B2C]/40"
          />
          {search && (
            <button onClick={() => setSearch('')} aria-label="Clear search" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <p className="text-xs text-gray-400 sm:ml-auto">
          {visibleRows.length} shown{filter !== 'all' || q ? ` of ${data.length}` : ''}
        </p>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        {isLoading ? (
          <div className="p-12 text-center text-gray-500">Loading attendance data…</div>
        ) : error ? (
          <div className="p-12 text-center text-red-500">Failed to load attendance data</div>
        ) : data.length === 0 ? (
          <div className="p-12 text-center text-gray-400">No staff members found</div>
        ) : visibleRows.length === 0 ? (
          <div className="p-12 text-center text-gray-400">{q ? `No employees match “${search}”.` : 'No matching staff members'}</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Check In
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Check Out
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {visibleRows.map((user) => (
                <tr key={user.user_id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {user.avatar_url ? (
                        <img
                          src={user.avatar_url}
                          alt={user.name}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-semibold text-gray-600">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <Link to={`/${orgSlug}/admin/users/${user.user_id}`} className="text-sm font-medium text-gray-900 hover:text-[#EC5B2C] hover:underline">{user.name}</Link>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                    {roleLabelMap[user.role] ?? user.role}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <StatusBadge user={user} />
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap font-mono">
                    {formatTime(user.checkin_time)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap font-mono">
                    {formatTime(user.checkout_time)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                    {formatDuration(user.duration_minutes)}
                    {user.sessions > 1 && (
                      <span className="ml-1 text-xs text-gray-400">×{user.sessions}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

/* ─── Admin: send a notification to everyone (+ optional Slack channel) ─── */
function SendNotificationModal({ onClose }: { onClose: () => void }) {
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [channelId, setChannelId] = useState('')
  const [result, setResult] = useState<{ recipients: number; channel_posted: boolean } | null>(null)
  const [error, setError] = useState('')

  const qc = useQueryClient()
  const { data: channels = [] } = useQuery({ queryKey: ['slack-channels'], queryFn: listSlackChannels })
  const { data: sent = [] } = useQuery({ queryKey: ['broadcasts'], queryFn: listBroadcasts })

  const mut = useMutation({
    mutationFn: () => sendNotification({ title: title.trim(), body: body.trim() || undefined, channel_id: channelId || undefined }),
    onSuccess: (r) => { setResult(r); qc.invalidateQueries({ queryKey: ['notifications'] }); qc.invalidateQueries({ queryKey: ['broadcasts'] }) },
    onError: (e: any) => setError(e?.response?.data?.detail || 'Failed to send'),
  })

  const delMut = useMutation({
    mutationFn: (id: string) => deleteBroadcast(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['broadcasts'] }); qc.invalidateQueries({ queryKey: ['notifications'] }) },
  })

  const input = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EC5B2C]/40'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b px-5 py-3 sticky top-0 bg-white">
          <div className="flex items-center gap-2 font-semibold text-gray-900"><Bell className="h-4 w-4 text-[#EC5B2C]" /> Send Notification</div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        {result ? (
          <div className="p-5 space-y-4 text-sm">
            <p className="text-gray-700">
              Sent to <span className="font-semibold">{result.recipients}</span> employee{result.recipients === 1 ? '' : 's'}
              {result.channel_posted ? ' and posted to the selected channel.' : channelId ? ' (channel post failed — check the bot is in that channel).' : '.'}
            </p>
            <div className="flex justify-end">
              <button onClick={onClose} className="rounded-lg bg-[#EC5B2C] px-4 py-2 text-white">Done</button>
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            {error && <div className="rounded bg-red-50 border border-red-200 text-red-600 text-sm px-3 py-2">{error}</div>}
            <label className="block">
              <span className="block text-xs font-medium text-gray-600 mb-1">Title</span>
              <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={255} placeholder="e.g. Office closed Friday" className={input} />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-gray-600 mb-1">Message <span className="font-normal text-gray-400">(optional)</span></span>
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} maxLength={2000} className={input} />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-gray-600 mb-1">Also post to Slack channel <span className="font-normal text-gray-400">(optional)</span></span>
              <select value={channelId} onChange={(e) => setChannelId(e.target.value)} className={input}>
                <option value="">— In-app only —</option>
                {channels.map((c) => <option key={c.id} value={c.id}>#{c.name}</option>)}
              </select>
            </label>
            <p className="text-xs text-gray-400">Everyone sees this in their notification bell. If you pick a channel, it's also posted there.</p>
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600">Cancel</button>
              <button
                disabled={!title.trim() || mut.isPending}
                onClick={() => { setError(''); mut.mutate() }}
                className="flex items-center gap-2 rounded-lg bg-[#EC5B2C] px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
              >
                {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Send
              </button>
            </div>

            {sent.length > 0 && (
              <div className="border-t pt-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Sent announcements</p>
                <ul className="space-y-1.5">
                  {sent.map((b) => (
                    <li key={b.id} className="flex items-start gap-2 rounded-lg border border-gray-100 px-3 py-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-800 truncate">{b.title}</p>
                        <p className="text-[11px] text-gray-400 flex items-center gap-2 flex-wrap">
                          <span>{new Date(b.created_at).toLocaleDateString()}</span>
                          <span>· {b.recipients} recipient{b.recipients === 1 ? '' : 's'}</span>
                          {b.channel_posted && <span className="inline-flex items-center gap-0.5 text-gray-500"><Hash className="h-3 w-3" />channel</span>}
                        </p>
                      </div>
                      <button
                        title="Delete from everyone's bell and the channel"
                        disabled={delMut.isPending}
                        onClick={() => { if (confirm(`Delete "${b.title}" from every notification bell${b.channel_posted ? ' and the Slack channel' : ''}?`)) delMut.mutate(b.id) }}
                        className="p-1.5 rounded text-red-500 hover:bg-red-50 disabled:opacity-50 shrink-0"
                      ><Trash2 className="h-4 w-4" /></button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
